import type { TouchEvent, WheelEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Quote } from '~/config/quotes'
import { CarouselButtonNext, LinkCtaNext } from '~/components-v2'

type QuoteCarouselProps = {
  quotes: Quote[]
  heading?: string
  tone?: 'blue' | 'green' | 'purple' | 'peach' | 'pink' | 'yellow'
  reverse?: boolean
  showDots?: boolean
  formatAuthor?: boolean
}

const isExternalHref = (href: string) => /^https?:\/\//.test(href)
const defaultCtaLabel = <>Read the case&nbsp;study</>
const logoPanelTones = ['blue', 'green', 'purple', 'peach'] as const

export const QuoteCarousel = ({
  quotes,
  heading,
  tone,
  reverse = false,
  showDots = false,
  formatAuthor = false,
}: QuoteCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const quoteRefs = useRef<(HTMLQuoteElement | null)[]>([])
  const stageRef = useRef<HTMLDivElement>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const wheelLockRef = useRef(false)

  useEffect(() => {
    quoteRefs.current = quoteRefs.current.slice(0, quotes.length)

    const measureQuotes = () => {
      const stage = stageRef.current
      if (!stage) return

      stage.style.removeProperty('--quote-carousel-quote-height')

      const maxHeight = quoteRefs.current.reduce((max, quote) => {
        if (!quote) return max
        return Math.max(max, quote.getBoundingClientRect().height)
      }, 0)

      if (maxHeight > 0) {
        stage.style.setProperty(
          '--quote-carousel-quote-height',
          `${Math.ceil(maxHeight)}px`,
        )
      }
    }

    const frame = window.requestAnimationFrame(measureQuotes)
    window.addEventListener('resize', measureQuotes)
    document.fonts?.ready.then(measureQuotes)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', measureQuotes)
    }
  }, [quotes])

  if (quotes.length === 0) return null

  const goToPrevious = useCallback(() => {
    setActiveIndex((current) =>
      current === 0 ? quotes.length - 1 : current - 1,
    )
  }, [quotes.length])

  const goToNext = useCallback(() => {
    setActiveIndex((current) =>
      current === quotes.length - 1 ? 0 : current + 1,
    )
  }, [quotes.length])

  const handleTouchStart = useCallback((event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    if (!touch) return

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    }
  }, [])

  const handleTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      if (!touchStartRef.current) return

      const touch = event.changedTouches[0]
      if (!touch) return

      const deltaX = touch.clientX - touchStartRef.current.x
      const deltaY = touch.clientY - touchStartRef.current.y
      touchStartRef.current = null

      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return

      if (deltaX < 0) {
        goToNext()
        return
      }

      goToPrevious()
    },
    [goToNext, goToPrevious],
  )

  const handleTouchCancel = useCallback(() => {
    touchStartRef.current = null
  }, [])

  const handleWheel = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      const horizontalDelta = event.deltaX
      const verticalDelta = event.deltaY

      if (
        wheelLockRef.current ||
        Math.abs(horizontalDelta) < 20 ||
        Math.abs(horizontalDelta) < Math.abs(verticalDelta)
      ) {
        return
      }

      event.preventDefault()
      wheelLockRef.current = true
      window.setTimeout(() => {
        wheelLockRef.current = false
      }, 450)

      if (horizontalDelta > 0) {
        goToNext()
        return
      }

      goToPrevious()
    },
    [goToNext, goToPrevious],
  )

  return (
    <section
      className="page-section page-section--lg quote-section quote-carousel"
      data-reverse={reverse ? 'true' : undefined}
      data-show-dots={showDots ? 'true' : undefined}
    >
      <div className="page-section__inner quote-carousel__inner">
        {heading && (
          <hgroup className="quote-carousel__header">
            <h3 className="heading-xl">{heading}</h3>
          </hgroup>
        )}

        <div
          className="quote-carousel__stage"
          onTouchCancel={handleTouchCancel}
          onTouchEnd={handleTouchEnd}
          onTouchStart={handleTouchStart}
          onWheel={handleWheel}
          ref={stageRef}
        >
          {quotes.map((quote, index) => (
            <article
              aria-hidden={index !== activeIndex}
              className="quote-carousel__slide"
              data-active={index === activeIndex ? 'true' : undefined}
              data-tone={tone ?? logoPanelTones[index % logoPanelTones.length]}
              key={`${quote.title}-${index}`}
            >
              <div className="quote-carousel__partner">
                {quote.logo && (
                  <img
                    alt={quote.title}
                    className={`quote-carousel__logo quote-carousel__logo--${quote.title.toLowerCase().replaceAll(' ', '-')}`}
                    src={quote.logo}
                  />
                )}

                {quote.cta && (
                  <LinkCtaNext
                    className="quote-carousel__cta quote-carousel__cta--partner"
                    external={isExternalHref(quote.cta)}
                    href={isExternalHref(quote.cta) ? quote.cta : undefined}
                    to={isExternalHref(quote.cta) ? undefined : quote.cta}
                  >
                    {quote.ctaLabel ? quote.ctaLabel : defaultCtaLabel}
                  </LinkCtaNext>
                )}
              </div>

              <blockquote
                className="quote-carousel__quote"
                ref={(element) => {
                  quoteRefs.current[index] = element
                }}
              >
                <p className="heading-s">{quote.quote}</p>
                <cite>
                  {(showDots || formatAuthor) &&
                  typeof quote.cite === 'string' &&
                  quote.cite.includes(',') ? (
                    <>
                      <strong className="quote-carousel__author-name">
                        {quote.cite.slice(0, quote.cite.indexOf(',') + 1)}
                      </strong>
                      <span className="quote-carousel__author-role">
                        {quote.cite.slice(quote.cite.indexOf(',') + 1).trim()}
                      </span>
                    </>
                  ) : (
                    quote.cite
                  )}
                </cite>
              </blockquote>

              {quote.cta && (
                <LinkCtaNext
                  className="quote-carousel__cta quote-carousel__cta--mobile"
                  external={isExternalHref(quote.cta)}
                  href={isExternalHref(quote.cta) ? quote.cta : undefined}
                  to={isExternalHref(quote.cta) ? undefined : quote.cta}
                >
                  {quote.ctaLabel ? quote.ctaLabel : defaultCtaLabel}
                </LinkCtaNext>
              )}
            </article>
          ))}
        </div>

        {showDots && quotes.length > 1 && (
          <div
            className="quote-carousel__dots"
            role="group"
            aria-label="Quote pagination"
          >
            {quotes.map((quote, index) => (
              <button
                key={`${quote.title}-${index}`}
                type="button"
                className="quote-carousel__dot"
                data-active={index === activeIndex ? 'true' : undefined}
                aria-label={`Go to quote ${index + 1} of ${quotes.length}`}
                aria-pressed={index === activeIndex}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        )}

        {quotes.length > 1 && (
          <div className="quote-carousel__controls carousel-controls-next">
            <CarouselButtonNext direction="previous" onClick={goToPrevious} />
            <CarouselButtonNext direction="next" onClick={goToNext} />
          </div>
        )}
      </div>
    </section>
  )
}
